import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaGlobe,
  FaLinkedin,
  FaTelegram,
  FaVk,
  FaYoutube,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import SiteLogo from './SiteLogo.tsx';
import './css/Footer.css';

const SOCIAL_LINKS = [
  { key: 'socialVk', icon: FaVk, href: 'https://vk.com/club239496741' },
  { key: 'socialTelegram', icon: FaTelegram, href: 'https://t.me' },
  { key: 'socialYoutube', icon: FaYoutube, href: 'https://youtube.com' },
  { key: 'socialLinkedin', icon: FaLinkedin, href: 'https://linkedin.com' },
  { key: 'socialX', icon: FaXTwitter, href: 'https://x.com' },
] as const;

function FooterLanguageSelect() {
  const { currentLanguage, setLanguage, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  const label = currentLanguage === 'ru' ? t('footer.languageRu') : t('footer.languageEn');

  return (
    <div className="site-footer__lang" ref={ref}>
      <button
        type="button"
        className="site-footer__lang-btn"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <FaGlobe aria-hidden />
        <span>{label}</span>
        <span className={`site-footer__lang-chevron ${open ? 'site-footer__lang-chevron--open' : ''}`}>
          ▼
        </span>
      </button>
      <div
        className={`site-footer__lang-menu ${open ? 'site-footer__lang-menu--open' : ''}`}
        role="listbox"
      >
        {(['ru', 'en'] as const).map((lang) => (
          <button
            key={lang}
            type="button"
            role="option"
            aria-selected={currentLanguage === lang}
            className={`site-footer__lang-option${
              currentLanguage === lang ? ' site-footer__lang-option--active' : ''
            }`}
            onClick={() => {
              setLanguage(lang);
              setOpen(false);
            }}
          >
            {lang === 'ru' ? t('footer.languageRu') : t('footer.languageEn')}
          </button>
        ))}
      </div>
    </div>
  );
}

function Footer() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  return (
    <footer className="site-footer">
      <div className="site-footer__inner">
        <div className="site-footer__main">
          <div className="site-footer__brand">
            <SiteLogo />
            <p className="site-footer__tagline">{t('footer.tagline')}</p>
            <div className="site-footer__social">
              {SOCIAL_LINKS.map(({ key, icon: Icon, href }) => (
                <a
                  key={key}
                  href={href}
                  className="site-footer__social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t(`footer.${key}`)}
                >
                  <Icon aria-hidden />
                </a>
              ))}
            </div>
          </div>

          <div className="site-footer__columns">
            <div>
              <h3 className="site-footer__col-title">{t('footer.colPlatform')}</h3>
              <ul className="site-footer__links">
                <li>
                  <Link to="/problems">{t('footer.linkTasks')}</Link>
                </li>
                <li>
                  <Link to="/leaderboard">{t('footer.linkLeaderboard')}</Link>
                </li>
                <li>
                  <Link to="/subscription">{t('footer.linkPricing')}</Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="site-footer__col-title">{t('footer.colCompany')}</h3>
              <ul className="site-footer__links">
                <li>
                  <Link to="/">{t('footer.linkAbout')}</Link>
                </li>
                <li>
                  <a href="mailto:info@sciplex.com">{t('footer.linkBlog')}</a>
                </li>
                <li>
                  <a href="mailto:info@sciplex.com">{t('footer.linkCareers')}</a>
                </li>
                <li>
                  <a href="mailto:info@sciplex.com">{t('footer.linkPartners')}</a>
                </li>
                <li>
                  <a href="mailto:info@sciplex.com">{t('footer.linkContact')}</a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="site-footer__col-title">{t('footer.colSupport')}</h3>
              <ul className="site-footer__links">
                <li>
                  <a href="mailto:info@sciplex.com">{t('footer.linkHelp')}</a>
                </li>
                <li>
                  <Link to="/faq">{t('footer.linkFaq')}</Link>
                </li>
                <li>
                  <Link to="/terms">{t('footer.linkRules')}</Link>
                </li>
                <li>
                  <Link to="/privacy">{t('footer.linkPrivacy')}</Link>
                </li>
                <li>
                  <Link to="/cookies-policy">{t('footer.linkCookies')}</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="site-footer__bar">
          <p className="site-footer__copyright">
            © {year} Sciplex. {t('footer.copyright')}
          </p>
          <FooterLanguageSelect />
        </div>
      </div>
    </footer>
  );
}

export default Footer;
